/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The full-screen invite, reached from the activation card.
 *
 * The route only feeds the form: which seats exist, who already holds one,
 * and where to go afterwards. Everything the operator can get wrong is
 * decided in `InviteTeam.rows`, which needs no page to be exercised.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Text } from '@/shared/ui';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type {
  InviteEntry, TeamOverview,
} from '@/services/requests/housien-services/AgenciesService.team';
import type { PlanFeatureType } from '@/features/pricing';
import { loadPlans } from '@/features/onboarding/hooks/plans-cache';
import { InviteTeam } from '@/features/onboarding/components/sections/InviteTeam';
import { SUPPORT_BLOCK_ID, planTitleFor } from '@/features/onboarding/components/sections/InviteTeam.rows';

/**
 * Invites the team into the seats the subscription bought.
 *
 * @returns The invite screen.
 */
export const InviteTeamPage: React.FC = () => {
  const { t } = useTranslation('@pages/onboarding');
  const navigate = useNavigate();
  const agency = useAgency((state) => state.agency);
  const [team, setTeam] = useState<TeamOverview | null>(null);
  const [catalogue, setCatalogue] = useState<PlanFeatureType[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!agency?.id) return;
    let live = true;
    AgenciesService.getAgencyTeam(agency.id)
      .then((overview) => { if (live) setTeam(overview); })
      .catch(() => { if (live) setFailed(true); });
    loadPlans()
      .then((plans) => { if (live) setCatalogue(plans.plans_features); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [agency?.id]);

  /* A seat block carries the catalogue's own id; the key is the fallback for
     a catalogue entry that never recorded one. */
  const planFor = useCallback((planId: string) => catalogue.find((plan) => (
    plan.catalogueId === planId || plan.key === planId
  )) ?? null, [catalogue]);

  const planTitle = useCallback(
    (planId: string) => planTitleFor(catalogue, planId),
    [catalogue],
  );

  const seatedEmails = useMemo(() => [
    ...(team?.people ?? []).map((person) => person.email),
    ...(team?.pending ?? []).map((invitation) => invitation.email),
  ], [team]);

  const owner = useMemo(
    () => (team?.people ?? []).find((person) => person.role === 'owner') ?? null,
    [team],
  );
  const ownerPlanTitle = owner?.plan_id ? planTitle(owner.plan_id) : null;

  const reloadTeam = useCallback(() => {
    if (!agency?.id) return;
    AgenciesService.getAgencyTeam(agency.id)
      .then(setTeam)
      .catch(() => setFailed(true));
  }, [agency?.id]);

  // The buyer picks which of the bought seats is theirs; their allowance is
  // sized by the seat, so the page cannot decide it for them.
  const chooseOwnerSeat = useCallback(async (planId: string) => {
    if (!agency?.id || !owner?.email) return false;
    const verdict = await AgenciesService.moveMemberSeat(agency.id, owner.email, planId)
      .catch(() => ({ ok: false, reason: 'send_failed', plan_id: planId }));
    if (!verdict.ok) {
      toast.error(t('inviteTeam.ownerPickFailed'));
      return false;
    }
    reloadTeam();
    return true;
  }, [agency?.id, owner?.email, reloadTeam, t]);

  // One tier bought leaves nothing to choose, so the seat is written here as
  // the gate does elsewhere — the gate stays off this route, so it cannot.
  const [autoSeated, setAutoSeated] = useState(false);
  useEffect(() => {
    const paid = team?.seats.by_plan ?? [];
    if (!owner || owner.plan_id || paid.length !== 1 || autoSeated) return;
    setAutoSeated(true);
    chooseOwnerSeat(paid[0].plan_id);
  }, [team, owner, autoSeated, chooseOwnerSeat]);

  const toTeamTab = () => navigate('../settings');

  const send = async (entries: InviteEntry[]) => {
    const result = await AgenciesService.inviteAgencyMembers(agency.id ?? '', entries);
    if (result.sent) {
      toast.success(t('inviteTeam.sentToast', { count: result.sent }));
    }
    // Only a clean batch leaves the screen: a refused line has to stay in
    // front of whoever typed it, or the operator walks away believing six
    // invitations went out when four did.
    if (result.sent === entries.length) toTeamTab();
    return result.results;
  };

  // The auxiliary places ride in as one more block, so the screen keeps a
  // single shape. They are free, so there is no plan to name them by and
  // no tier to group them under: one block, however many tiers paid for it.
  const supportSeats = team?.support_seats ?? null;
  const blocks = [
    ...(team?.seats.by_plan ?? []),
    ...(supportSeats && supportSeats.total > 0
      ? [{
        plan_id: SUPPORT_BLOCK_ID,
        bought: supportSeats.total,
        taken: supportSeats.used,
        free: supportSeats.free,
      }]
      : []),
  ];

  return (
    <main
      style={{
        maxWidth: 600, margin: '0 auto', width: '100%',
        padding: 'var(--space-12) var(--space-6) var(--space-12)',
      }}
    >
      {failed && (
        <Text style={{ color: 'var(--status-error-text)', fontSize: 'var(--text-sm)' }}>
          {t('inviteTeam.loadFailed')}
        </Text>
      )}
      {!failed && team && blocks.length === 0 && (
        <Text style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
          {t('inviteTeam.noSeats')}
        </Text>
      )}
      {!failed && team && blocks.length > 0 && (
        <InviteTeam
          blocks={blocks}
          planTitle={planTitle}
          planFor={planFor}
          seatedEmails={seatedEmails}
          ownerPlanTitle={ownerPlanTitle}
          onChooseOwnerSeat={owner ? chooseOwnerSeat : undefined}
          onSend={send}
          onSkip={toTeamTab}
          onGoToPlan={() => navigate('../settings?section=plano')}
        />
      )}
    </main>
  );
};

export default InviteTeamPage;
